import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Elements,
    PaymentElement,
    PaymentRequestButtonElement,
    useStripe,
} from '@stripe/react-stripe-js';
import { stripePromise } from '../utils/stripe';

function RemoveAdsPaymentInner({
    clientSecret,
    amount,
    currency,
    onComplete,
    onCancel,
    onProcessingChange,
    onCheckoutFallback,
    isCheckoutLoading,
}) {
    const stripe = useStripe();
    const [message, setMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentRequest, setPaymentRequest] = useState(null);
    const closeBtnRef = useRef(null);

    const updateProcessingState = useCallback((next) => {
        setIsProcessing(next);
        if (onProcessingChange) {
            onProcessingChange(next);
        }
    }, [onProcessingChange]);

    useEffect(() => {
        if (!stripe || !clientSecret) {
            return undefined;
        }

        const pr = stripe.paymentRequest({
            country: 'US',
            currency,
            total: {
                label: 'Remove ads forever',
                amount,
            },
            requestPayerName: true,
            requestPayerEmail: true,
        });

        pr.canMakePayment().then((result) => {
            if (result) {
                setPaymentRequest(pr);
            }
        });

        return () => {
            pr.off('paymentmethod');
        };
    }, [stripe, clientSecret, amount, currency]);

    useEffect(() => {
        if (!paymentRequest || !stripe) {
            return undefined;
        }

        const handlePaymentMethod = async (event) => {
            updateProcessingState(true);
            setMessage('');

            try {
                const { error: confirmError } = await stripe.confirmCardPayment(
                    clientSecret,
                    {
                        payment_method: event.paymentMethod.id,
                    },
                    { handleActions: false }
                );

                if (confirmError) {
                    event.complete('fail');
                    setMessage(confirmError.message || 'Unable to complete payment.');
                    return;
                }

                event.complete('success');

                const { error: followUpError } = await stripe.confirmCardPayment(clientSecret);
                if (followUpError) {
                    setMessage(followUpError.message || 'Additional verification required to finish payment.');
                    return;
                }

                await onComplete();
            } catch (err) {
                setMessage(err.message || 'Unexpected error while processing payment.');
                event.complete('fail');
            } finally {
                updateProcessingState(false);
            }
        };

        paymentRequest.on('paymentmethod', handlePaymentMethod);

        return () => {
            paymentRequest.off('paymentmethod', handlePaymentMethod);
        };
    }, [paymentRequest, stripe, clientSecret, onComplete, updateProcessingState]);

    return (
        <div className="payment-sheet" role="dialog" aria-modal="true" aria-labelledby="payment-sheet-title">
            <div className="payment-sheet__header">
                <h5 id="payment-sheet-title">Complete purchase</h5>
                <button ref={closeBtnRef} type="button" className="btn-close" aria-label="Close payment form" onClick={onCancel} disabled={isProcessing} />
            </div>

            {paymentRequest && (
                <div className="payment-sheet__wallet mb-3">
                    <PaymentRequestButtonElement
                        options={{
                            paymentRequest,
                            style: {
                                paymentRequestButton: {
                                    type: 'default',
                                    theme: 'dark',
                                    height: '48px',
                                },
                            },
                        }}
                        className="payment-wallet-button"
                    />
                    <p className="text-muted small mb-0">
                        Use Apple Pay, Google Pay, or another supported wallet when available.
                    </p>
                </div>
            )}

            <div className="payment-sheet__form">
                <PaymentElement options={{ layout: 'tabs' }} />
                {message && <div className="alert alert-info mt-3" role="status">{message}</div>}
                <div className="d-flex gap-2 mt-3">
                    <button type="button" className="btn btn-success" onClick={onCheckoutFallback} disabled={isProcessing || isCheckoutLoading}>
                        {isCheckoutLoading ? 'Opening checkout...' : 'Use secure checkout'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function RemoveAdsPayment({
    clientSecret,
    amount = 100,
    currency = 'usd',
    onComplete,
    onCancel,
    onProcessingChange,
    onCheckoutFallback,
    isCheckoutLoading,
    isDarkMode,
}) {
    const appearance = useMemo(() => ({
        theme: isDarkMode ? 'night' : 'stripe',
        variables: {
            colorPrimary: '#f97316',
            colorBackground: isDarkMode ? '#101827' : '#ffffff',
            colorText: isDarkMode ? '#f8fafc' : '#1f2937',
        },
    }), [isDarkMode]);

    return (
        <Elements
            stripe={stripePromise}
            options={{
                clientSecret,
                appearance,
            }}
        >
            <RemoveAdsPaymentInner
                clientSecret={clientSecret}
                amount={amount}
                currency={currency}
                onComplete={onComplete}
                onCancel={onCancel}
                onProcessingChange={onProcessingChange}
                onCheckoutFallback={onCheckoutFallback}
                isCheckoutLoading={isCheckoutLoading}
            />
        </Elements>
    );
}

export default RemoveAdsPayment;
